import { getAllZenDeskTagsInArray } from "@faire/help-center-self-serve/utils/zenDeskTags";
import { BRAND_TYPE } from "@faire/help-center-utils/consts/BRAND_TYPE";
import { RETAILER_TYPE } from "@faire/help-center-utils/consts/RETAILER_TYPE";
import { getBrand } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getUser } from "@faire/help-center-utils/serialized-data/getUser";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { ICreateTicketRequest } from "@faire/web-api--source/com/faire/vendor/zendesk/ICreateTicketRequest";
import { IUploadResponse } from "@faire/web-api--source/com/faire/vendor/zendesk/IUploadResponse";
import uploadAttachment from "@faire/web-api--source/endpoints/www/api/zendesk/uploads/post";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import UAParser from "ua-parser-js";

import { orderNumberRegex, weakEmailRegex, weakFaireUrlRegex } from "./regex";

export interface IEmailUsFormValues {
  name: string;
  email: string;
  subject: string;
  orderNumber: string;
  description: string;
  websiteUrl: string;
}

type IEmailUsFormErrors = {
  [Property in keyof IEmailUsFormValues]?: string;
};

export const validateForm = (
  values: IEmailUsFormValues,
  strictLocalize: StrictLocalizeFunction
): IEmailUsFormErrors => {
  const errors: IEmailUsFormErrors = {};

  if (values.name.trim().length === 0) {
    errors.name = strictLocalize({
      defaultMessage: "Please enter your full name",
      description: {
        text: "Error message when the name is empty",
      },
    });
  }

  if (values.email.trim().length === 0) {
    errors.email = strictLocalize({
      defaultMessage: "Please enter an email",
    });
  } else if (!weakEmailRegex.test(values.email)) {
    errors.email = strictLocalize({
      defaultMessage: "Email must be a valid email",
    });
  }

  if (values.subject.trim().length === 0) {
    errors.subject = strictLocalize({
      defaultMessage: "Please enter a subject",
    });
  }

  if (values.description.length === 0) {
    errors.description = strictLocalize({
      defaultMessage: "Please enter a description",
    });
  }

  if (
    values.websiteUrl.trim().length > 0 &&
    !weakFaireUrlRegex.test(values.websiteUrl.trim())
  ) {
    errors.websiteUrl = strictLocalize({
      defaultMessage: "Please enter a valid Faire URL",
    });
  }

  return errors;
};

export const uploadAttachmentIfAny = async (attachment: File | undefined) => {
  if (attachment) {
    try {
      const formData = new FormData();
      formData.append("file", attachment);
      const response: IUploadResponse = await uploadAttachment(formData, {
        filename: attachment.name,
      });
      return response.upload?.token;
    } catch (error) {
      return undefined;
    }
  }
  return undefined;
};

export const getOrderTokenFromField = (
  selectedBrandOrder: ISelfServeBrandOrder | undefined,
  orderNumber: string
) => {
  const orderNumberInput = orderNumber.trim();
  const orderToken =
    orderNumberInput.toLowerCase() !==
      selectedBrandOrder?.order_number?.toLowerCase() &&
    orderNumberRegex.test(orderNumberInput.trim())
      ? `o_${orderNumberInput.toLowerCase()}`
      : selectedBrandOrder?.brand_order?.order_items[0]?.order_token;
  return orderToken;
};

interface ITicketPayloadRequest {
  selectedBrandOrder: ISelfServeBrandOrder | undefined;
  values: IEmailUsFormValues;
  issueType: string;
  attachmentToken: string | undefined;
}

export const getTicketPayload = ({
  selectedBrandOrder,
  values,
  issueType,
  attachmentToken,
}: ITicketPayloadRequest) => {
  const retailer = getRetailer();
  const brand = getBrand();
  const user = getUser();
  const ua = new UAParser(navigator.userAgent).getResult();
  const platform = ua.browser.name ?? "";
  const osVersion = `${ua.os.name ?? ""} ${ua.os.version ?? ""}`;
  const appVersion = ua.browser.version ?? "";
  const customerType = retailer ? RETAILER_TYPE : BRAND_TYPE;
  const businessName = retailer?.name ?? brand?.name;
  const customerBasedIn =
    retailer?.country ?? brand?.based_in ?? ISO3166Alpha3.USA;
  const orderToken = getOrderTokenFromField(
    selectedBrandOrder,
    values.orderNumber
  );
  const brandOrderToken = selectedBrandOrder?.brand_order?.token ?? "";
  const ticketPayload = ICreateTicketRequest.build({
    tags: getAllZenDeskTagsInArray(
      customerBasedIn ?? "",
      customerType ?? "",
      issueType ?? ""
    ),
    email: values.email,
    user_name: values.name,
    name: businessName,
    subject: values.subject,
    brand_order_token: brandOrderToken,
    issue_type: issueType,
    description: values.description,
    web_url: values.websiteUrl,
    platform: platform,
    os_version: osVersion,
    app_version: appVersion,
    attachment_token: attachmentToken,
    order_token: orderToken,
    retailer_token: retailer?.token,
    brand_token: brand?.token,
    external_id: user?.token,
  });

  return {
    ticketPayload,
  };
};
